import { Player } from "@lottiefiles/react-lottie-player";
import { useNavigate } from "react-router-dom";
import { ExerciseType } from "@/components/v3/exercises/ExerciseBadge";
import { useExerciseHandlers } from "@/utils/hooks/useExerciseHandlers";
import { renderExerciseTypePot } from "@/utils/exercises";
import { calculateAverageScore } from "../Desktop/ExerciseV2Modal";
import { ExerciseOptionTypes, Submission } from "@/types";
import {
  useExerciseState,
  useExerciseAction,
} from "@/contexts/exercise/Context";
import routes from "@/config/routes";
import Button from "@edusogno/primitives/button";
import alien1 from "@/assets/animations/alien1.json";
import alien2 from "@/assets/animations/alien2.json";
import alien3 from "@/assets/animations/alien3.json";
import ScoreBar from "../Desktop/ScoreBar";
import ExerciseLayout from "@/layouts/ExerciseLayout";

interface Data {
  score: number;
  total: number;
  exerciseType: ExerciseOptionTypes;
  submissionData: Submission[];
}

type Props = {
  data: Data;
  onClose: () => void;
};

const GrammarExerciseResult = ({ data, onClose }: Props) => {
  const navigate = useNavigate();
  const { exerciseResults, totalExercises, viewResults } = useExerciseState();
  const { handleUpdateExerciseStatus, handleSaveProgress } =
    useExerciseHandlers();
  const { nextExercise, updateExerciseResult, endSequence, setViewResults } =
    useExerciseAction();

  const { submissionData, score, total, exerciseType } = data;

  const percentage = Math.round((score / total) * 100);

  const isLastExercise = exerciseResults.length >= totalExercises - 1;

  const formattedType = renderExerciseTypePot(exerciseType);

  const handleNextExerciseClick = async () => {
    const newResult = { type: exerciseType, score, total };

    const resultExists = exerciseResults.some((r) => r.type === exerciseType);

    if (!resultExists) {
      updateExerciseResult(newResult);
    }

    try {
      if (isLastExercise) {
        await handleSaveProgress(
          submissionData,
          exerciseType,
          newResult,
          calculateAverageScore(exerciseResults),
        );

        await handleUpdateExerciseStatus("completed");

        setViewResults(true);
      } else {
        await handleSaveProgress(submissionData, exerciseType, newResult, 0);

        await handleUpdateExerciseStatus("in progress");

        onClose?.();
        nextExercise();
      }
    } catch (error) {
      console.error("Failed to process exercise transition:", error);
    }
  };

  const handleClose = () => {
    endSequence();
    onClose?.();
    navigate(routes.exercises);
  };

  const animation =
    percentage <= 20 ? alien1 : percentage <= 90 ? alien2 : alien3;

  const getMessage = () => {
    if (percentage > 50) {
      return "Amazing! An excellent result! Well done!";
    } else if (percentage <= 50 && percentage > 20) {
      return "Maybe you just weren't quite focused enough";
    } else if (percentage <= 20) {
      return "Come on, you can definitely do better!";
    }
  };

  return (
    <div className="fixed w-full h-full inset-0 z-[999]">
      <ExerciseLayout
        isResult
        isGrammar
        isCorrection
        type={ExerciseType.COMPREHENSION}
        title={formattedType}
        onClose={handleClose}
      >
        <div className="flex flex-col w-full h-full gap-4 pb-4">
          <div className="bg-white px-4 gap-4 text-black flex flex-col rounded-[10px] w-full h-full py-[2rem]">
            {viewResults ? (
              <>
                <p className="font-semibold text-center text-xl leading-[120%]">
                  Your results 💯
                </p>

                <div className="flex flex-col gap-4 h-full w-full">
                  {exerciseResults.map((r, i) => (
                    <div key={r.type + i} className="mt-3 w-full">
                      <ScoreBar type={r.type} score={r.score} total={r.total} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <Player
                  loop
                  autoplay
                  src={animation}
                  style={{
                    width: "250px",
                    height: "250px",
                    marginTop: "2rem",
                  }}
                />

                <p className="font-semibold text-center text-xl mt-auto leading-[120%]">
                  {getMessage()}
                </p>

                <p className="text-sm text-center">
                  Your score is&nbsp;
                  <i className="not-italic font-semibold">
                    {score} out of {total}
                  </i>
                  &nbsp;({percentage}%)
                </p>
              </>
            )}
          </div>

          {viewResults ? (
            <Button variant="outlined" color="white" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <Button
              onClick={
                isLastExercise && viewResults
                  ? handleClose
                  : handleNextExerciseClick
              }
              className={`px-12 py-3 border ${
                isLastExercise && viewResults
                  ? ""
                  : isLastExercise
                  ? "bg-white text-brand-secondary border-brand-secondary"
                  : "border-brand"
              }`}
            >
              {isLastExercise && viewResults
                ? "Close"
                : isLastExercise
                ? "View results ⭐"
                : "Next exercise"}
            </Button>
          )}
        </div>
      </ExerciseLayout>
    </div>
  );
};

export default GrammarExerciseResult;
